import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Users, CalendarDays, BookOpen, X, ChevronDown, ChevronUp, Send, GraduationCap, ClipboardCheck, UploadCloud, Video, Presentation, UserCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const M = `${API}/itineraries/manage`;

const EMPTY_SESSION = { title: "", doc_url: "", doc_name: "", ppt_url: "", ppt_name: "", video_url: "", notes: "" };

const STATUS_LABEL = {
  draft: { label: "Rascunho", klass: "bg-amber-100 text-amber-800" },
  published: { label: "Publicada", klass: "bg-emerald-100 text-emerald-800" },
  archived: { label: "Arquivada", klass: "bg-gray-100 text-gray-700" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

// Upload de documento de estudo → object storage → devolve URL público.
function DocUploadButton({ onUploaded, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = { current: null };
  const pick = () => inputRef.current?.click();
  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${M}/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data.url, r.data.name);
      toast.success(`"${r.data.name}" carregado.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload.");
    } finally { setBusy(false); ev.target.value = ""; }
  };
  return (
    <>
      <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" className="hidden"
        ref={(el) => { inputRef.current = el; }} onChange={onFile} />
      <button type="button" onClick={pick} disabled={busy}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-[10px] uppercase tracking-[0.16em] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors disabled:opacity-60"
        title="Carregar PDF/DOC/PPT (máx. 20MB)" data-testid={testId}>
        <UploadCloud className="h-3.5 w-3.5" /> {busy ? "A carregar…" : "Carregar"}
      </button>
    </>
  );
}


// ─── Template form (create / edit) ──────────────────────────────────────────
function TemplateForm({ initial, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [sessions, setSessions] = useState(
    initial?.sessions?.length
      ? initial.sessions.map((s) => ({ title: s.title || "", doc_url: s.doc_url || "", doc_name: s.doc_name || "", ppt_url: s.ppt_url || "", ppt_name: s.ppt_name || "", video_url: s.video_url || "", notes: s.notes || "" }))
      : Array.from({ length: 8 }, (_, i) => ({ ...EMPTY_SESSION, title: `Sessão ${i + 1}` }))
  );
  const [busy, setBusy] = useState(false);

  const setS = (i, patch) => setSessions((arr) => arr.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSession = () => setSessions((arr) => [...arr, { ...EMPTY_SESSION, title: `Sessão ${arr.length + 1}` }]);
  const removeSession = (i) => setSessions((arr) => arr.filter((_, j) => j !== i));

  const save = async () => {
    if (!title.trim()) { toast.error("Indique o título do percurso."); return; }
    if (!sessions.length || sessions.some((s) => !s.title.trim())) { toast.error("Todas as sessões precisam de título."); return; }
    setBusy(true);
    try {
      const payload = { title, description, sessions };
      if (initial?.id) {
        await axios.put(`${M}/templates/${initial.id}`, payload, { withCredentials: true });
        toast.success("Modelo atualizado.");
      } else {
        await axios.post(`${M}/templates`, payload, { withCredentials: true });
        toast.success("Modelo criado.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar o modelo.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[hsl(var(--accent))] p-6 space-y-4" data-testid="itinerary-template-form">
      <div className="overline">{initial?.id ? "Editar modelo" : "Novo modelo de percurso"}</div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Título *</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Percurso de Auto-Educação I"
          className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="template-title" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Descrição</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="template-description" />
      </div>
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Sessões ({sessions.length})</div>
        {sessions.map((s, i) => (
          <div key={i} className="border border-[hsl(var(--border))] p-3 grid sm:grid-cols-2 gap-2" data-testid={`template-session-${i + 1}`}>
            <div className="sm:col-span-2 flex items-center gap-2">
              <span className="text-xs font-medium text-[hsl(var(--primary))] shrink-0">Sessão {i + 1}</span>
              <input value={s.title} onChange={(e) => setS(i, { title: e.target.value })} placeholder="Título da sessão *"
                className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-sm" data-testid={`session-title-${i + 1}`} />
              {sessions.length > 1 && (
                <button onClick={() => removeSession(i)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" title="Remover sessão">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input value={s.doc_url} onChange={(e) => setS(i, { doc_url: e.target.value })} placeholder="Documento de estudo — carregue o ficheiro ou cole um URL"
                className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-docurl-${i + 1}`} />
              <DocUploadButton testId={`session-upload-${i + 1}`}
                onUploaded={(url, name) => setS(i, { doc_url: url, doc_name: s.doc_name || name })} />
            </div>
            <input value={s.doc_name} onChange={(e) => setS(i, { doc_name: e.target.value })} placeholder="Nome do documento (opcional)"
              className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" />
            <div className="flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
              <input value={s.video_url} onChange={(e) => setS(i, { video_url: e.target.value })} placeholder="Link de vídeo para ver antes (YouTube, Vimeo…)"
                className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-video-${i + 1}`} />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Presentation className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
              <input value={s.ppt_url} onChange={(e) => setS(i, { ppt_url: e.target.value })} placeholder="PowerPoint do educador — enviado apenas ao educador da sessão"
                className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-ppturl-${i + 1}`} />
              <DocUploadButton testId={`session-ppt-upload-${i + 1}`}
                onUploaded={(url, name) => setS(i, { ppt_url: url, ppt_name: s.ppt_name || name })} />
            </div>
          </div>
        ))}
        <button onClick={addSession} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]" data-testid="template-add-session">
          <Plus className="h-3.5 w-3.5" /> Adicionar sessão
        </button>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="template-save">
          {busy ? "A guardar…" : initial?.id ? "Guardar alterações" : "Criar modelo"}
        </button>
        <button onClick={onCancel} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">Cancelar</button>
      </div>
    </div>
  );
}

// ─── Attendance panel for one session ───────────────────────────────────────
function AttendancePanel({ editionId, session, participants, onSaved }) {
  const [checked, setChecked] = useState(new Set(session.attendance || []));
  const [busy, setBusy] = useState(false);
  const toggle = (mid) => setChecked((s) => {
    const n = new Set(s); n.has(mid) ? n.delete(mid) : n.add(mid); return n;
  });
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${M}/editions/${editionId}/sessions/${session.index}/attendance`,
        { member_ids: [...checked] }, { withCredentials: true });
      toast.success(`Presenças da sessão ${session.index} guardadas.`);
      onSaved();
    } catch { toast.error("Erro a guardar presenças."); } finally { setBusy(false); }
  };
  return (
    <div className="sm:col-span-4 bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-3" data-testid={`attendance-panel-${session.index}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">Presenças — Sessão {session.index}</div>
      {participants.length === 0 ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Sem participantes inscritos.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-1.5">
          {participants.map((p) => (
            <label key={p.member_id} className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={checked.has(p.member_id)} onChange={() => toggle(p.member_id)}
                data-testid={`attendance-check-${session.index}-${p.member_id}`} />
              {p.name}
            </label>
          ))}
        </div>
      )}
      <button onClick={save} disabled={busy || !participants.length}
        className="mt-3 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 bg-[hsl(var(--primary))] text-white disabled:opacity-50"
        data-testid={`attendance-save-${session.index}`}>
        {busy ? "A guardar…" : "Guardar presenças"}
      </button>
    </div>
  );
}

// ─── Certificates section (eligibility ≥75% + issue) ────────────────────────
function CertificatesSection({ editionId, refreshKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${M}/editions/${editionId}/certificates`, { withCredentials: true });
      setData(r.data);
    } catch { toast.error("Erro a carregar certificados."); }
  }, [editionId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const issue = async () => {
    if (!window.confirm("Emitir certificados para todos os participantes elegíveis (assiduidade ≥75%)? Cada um recebe o PDF por email.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${M}/editions/${editionId}/certificates`, {}, { withCredentials: true });
      const c = r.data.counts;
      toast.success(`${c.issued} certificado(s) emitido(s)${c.not_eligible ? ` · ${c.not_eligible} sem assiduidade` : ""}${c.skipped ? ` · ${c.skipped} já emitidos` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a emitir."); } finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <div data-testid="certificates-section">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
        Certificados de conclusão (assiduidade ≥ {data.min_attendance_pct}%)
      </div>
      {data.eligibility.length === 0 ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Sem participantes.</p>
      ) : (
        <ul className="space-y-1">
          {data.eligibility.map((el) => (
            <li key={el.member_id} className="flex items-center justify-between text-sm border border-[hsl(var(--border))] px-3 py-1.5" data-testid={`eligibility-${el.member_id}`}>
              <span>{el.name} <span className="text-xs text-[hsl(var(--muted-foreground))]">({el.attended}/{el.total} sessões)</span></span>
              {el.issued ? (
                <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-700">Emitido ✓</span>
              ) : el.eligible ? (
                <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]">Elegível</span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">Sem assiduidade</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.certificates.length > 0 && (
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
          Emitidos: {data.certificates.map((c) => c.code).join(", ")}
        </p>
      )}
      <button onClick={issue} disabled={busy}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--accent))] text-[hsl(var(--accent-foreground,var(--primary)))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--accent))] hover:text-white transition-colors disabled:opacity-60"
        data-testid="issue-certificates">
        <GraduationCap className="h-3.5 w-3.5" /> {busy ? "A emitir…" : "Emitir certificados"}
      </button>
    </div>
  );
}

// ─── Edition editor (dates, participants, status) ───────────────────────────
function EditionEditor({ edition, members, onChanged, onClose }) {
  const [e, setE] = useState(edition);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [openAttendance, setOpenAttendance] = useState(null); // session index
  const [certsRefresh, setCertsRefresh] = useState(0);

  const refresh = async () => {
    const r = await axios.get(`${M}/editions/${e.id}`, { withCredentials: true });
    setE(r.data);
    setCertsRefresh((k) => k + 1);
  };

  const setSession = (i, patch) =>
    setE((cur) => ({ ...cur, sessions: cur.sessions.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const payload = {
        title: e.title,
        doc_days_before: Number(e.doc_days_before) || 0,
        sig1_name: e.sig1_name || "", sig1_role: e.sig1_role || "",
        sig2_name: e.sig2_name || "", sig2_role: e.sig2_role || "",
        sessions: e.sessions.map((s) => ({
          title: s.title, doc_url: s.doc_url || "", doc_name: s.doc_name || "",
          ppt_url: s.ppt_url || "", ppt_name: s.ppt_name || "",
          educator_member_id: s.educator_member_id || null,
          video_url: s.video_url || "",
          notes: s.notes || "", date: s.date || null, time: s.time || null, location: s.location || "",
        })),
        ...(e.capacity ? { capacity: Number(e.capacity) } : { clear_capacity: true }),
        ...extra,
      };
      const r = await axios.put(`${M}/editions/${e.id}`, payload, { withCredentials: true });
      // Mantém os valores em edição no ecrã; só sincroniza estado e flags de envio
      // (evita o "flash" de inputs vazios enquanto o React re-renderiza).
      setE((cur) => ({
        ...cur,
        status: r.data.status,
        updated_at: r.data.updated_at,
        sessions: cur.sessions.map((s, i) => ({
          ...s,
          ...(r.data.sessions[i]
            ? {
                doc_email_sent_at: r.data.sessions[i].doc_email_sent_at,
                reminder_day_sent_at: r.data.sessions[i].reminder_day_sent_at,
                reminder_2h_sent_at: r.data.sessions[i].reminder_2h_sent_at,
                educator_email_sent_at: r.data.sessions[i].educator_email_sent_at,
                educator_name: r.data.sessions[i].educator_name,
              }
            : {}),
        })),
      }));
      onChanged();
      toast.success("Edição guardada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar.");
    } finally { setBusy(false); }
  };

  const addParticipant = async () => {
    if (!pick) return;
    try {
      await axios.post(`${M}/editions/${e.id}/participants`, { member_id: pick }, { withCredentials: true });
      const r = await axios.get(`${M}/editions/${e.id}`, { withCredentials: true });
      setE(r.data); setPick(""); onChanged();
      toast.success("Sócio inscrito — recebeu email de confirmação.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro a inscrever."); }
  };

  const removeParticipant = async (mid) => {
    try {
      await axios.delete(`${M}/editions/${e.id}/participants/${mid}`, { withCredentials: true });
      const r = await axios.get(`${M}/editions/${e.id}`, { withCredentials: true });
      setE(r.data); onChanged();
    } catch { toast.error("Erro a remover."); }
  };

  const enrolledIds = new Set((e.participants || []).map((p) => p.member_id));
  const pickable = members.filter((m) => !enrolledIds.has(m.id));

  return (
    <div className="bg-white border border-[hsl(var(--accent))] p-6 space-y-5" data-testid="edition-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{e.itinerary_title}</div>
          <h3 className="display-heading text-2xl mt-1">{e.title}</h3>
        </div>
        <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="edition-close">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Nome da edição</label>
          <input value={e.title} onChange={(ev) => setE({ ...e, title: ev.target.value })}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="edition-title" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Documento — dias de antecedência</label>
          <input type="number" min="0" max="60" value={e.doc_days_before}
            onChange={(ev) => setE({ ...e, doc_days_before: ev.target.value })}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="edition-days-before" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Lotação (vazio = sem limite)</label>
          <input type="number" min="1" value={e.capacity || ""}
            onChange={(ev) => setE({ ...e, capacity: ev.target.value })}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="edition-capacity" />
        </div>
      </div>

      {/* Sessões: datas, horas, locais */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
          <CalendarDays className="inline h-3 w-3 mr-1" /> Sessões — datas, horas e locais
        </div>
        <div className="space-y-2">
          {e.sessions.map((s, i) => (
            <div key={i} className="border border-[hsl(var(--border))] p-3 grid sm:grid-cols-4 gap-2 items-center" data-testid={`edition-session-${s.index}`}>
              <div className="sm:col-span-4 text-sm font-medium flex items-center flex-wrap gap-2">
                <span>{s.index}. {s.title}</span>
                {s.doc_url && <span className="text-[11px] text-[hsl(var(--accent))]">(tem documento)</span>}
                {s.video_url && <span className="text-[11px] text-[hsl(var(--accent))]">(tem vídeo)</span>}
                {s.ppt_url && <span className="text-[11px] text-[hsl(var(--accent))]">(tem PPT)</span>}
                {s.doc_email_sent_at && <span className="text-[10px] uppercase text-emerald-700">doc enviado ✓</span>}
                {s.educator_email_sent_at && <span className="text-[10px] uppercase text-emerald-700">ppt ao educador ✓</span>}
                {s.date && (
                  <button onClick={() => setOpenAttendance(openAttendance === s.index ? null : s.index)}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
                    data-testid={`attendance-toggle-${s.index}`}>
                    <ClipboardCheck className="h-3.5 w-3.5" /> Presenças ({(s.attendance || []).length})
                    {openAttendance === s.index ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                )}
              </div>
              <input type="date" value={s.date || ""} onChange={(ev) => setSession(i, { date: ev.target.value })}
                className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-date-${s.index}`} />
              <input type="time" value={s.time || ""} onChange={(ev) => setSession(i, { time: ev.target.value })}
                className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-time-${s.index}`} />
              <input value={s.location || ""} onChange={(ev) => setSession(i, { location: ev.target.value })} placeholder="Local"
                className="sm:col-span-2 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-location-${s.index}`} />
              <div className="sm:col-span-2 flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" title="Educador da sessão" />
                <select value={s.educator_member_id || ""} onChange={(ev) => setSession(i, { educator_member_id: ev.target.value || null })}
                  className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`session-educator-${s.index}`}>
                  <option value="">— Educador da sessão (sócio ou assessor) —</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{m.ciees_role === "assessor_espiritual" ? " · Assessor Espiritual" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <Presentation className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" title="PowerPoint do educador" />
                <input value={s.ppt_url || ""} onChange={(ev) => setSession(i, { ppt_url: ev.target.value })}
                  placeholder="PowerPoint do educador (só ele o recebe)"
                  className="flex-1 bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid={`edition-session-ppt-${s.index}`} />
                <DocUploadButton testId={`edition-ppt-upload-${s.index}`}
                  onUploaded={(url, name) => setSession(i, { ppt_url: url, ppt_name: s.ppt_name || name })} />
              </div>
              {openAttendance === s.index && (
                <AttendancePanel editionId={e.id} session={s} participants={e.participants || []} onSaved={refresh} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Participantes */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
          <Users className="inline h-3 w-3 mr-1" /> Participantes ({(e.participants || []).length}{e.capacity ? ` / ${e.capacity}` : ""})
        </div>
        <div className="flex gap-2 items-center">
          <select value={pick} onChange={(ev) => setPick(ev.target.value)}
            className="flex-1 bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="participant-picker">
            <option value="">— Escolher sócio —</option>
            {pickable.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
          </select>
          <button onClick={addParticipant} className="btn-primary" data-testid="participant-add">
            <Plus className="h-3.5 w-3.5" /> Inscrever
          </button>
        </div>
        {(e.participants || []).length > 0 && (
          <ul className="mt-3 space-y-1">
            {e.participants.map((p) => (
              <li key={p.member_id} className="flex items-center justify-between text-sm border border-[hsl(var(--border))] px-3 py-2" data-testid={`participant-${p.member_id}`}>
                <span>{p.name} <span className="text-xs text-[hsl(var(--muted-foreground))]">({p.email})</span></span>
                <button onClick={() => removeParticipant(p.member_id)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" data-testid={`participant-remove-${p.member_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Assinaturas do certificado */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
          <GraduationCap className="inline h-3 w-3 mr-1" /> Assinaturas do certificado
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={e.sig1_name || ""} onChange={(ev) => setE({ ...e, sig1_name: ev.target.value })} placeholder="Assinatura 1 — nome (Pe. Borja)"
            className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid="sig1-name" />
          <input value={e.sig1_role || ""} onChange={(ev) => setE({ ...e, sig1_role: ev.target.value })} placeholder="Assinatura 1 — cargo"
            className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid="sig1-role" />
          <input value={e.sig2_name || ""} onChange={(ev) => setE({ ...e, sig2_name: ev.target.value })} placeholder="Assinatura 2 — nome (D. Rui Valério)"
            className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid="sig2-name" />
          <input value={e.sig2_role || ""} onChange={(ev) => setE({ ...e, sig2_role: ev.target.value })} placeholder="Assinatura 2 — cargo"
            className="bg-white border border-[hsl(var(--border))] px-2 py-1.5 text-xs" data-testid="sig2-role" />
        </div>
      </div>

      {/* Certificados */}
      <CertificatesSection editionId={e.id} refreshKey={certsRefresh} />

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[hsl(var(--border))]">
        <button onClick={() => save()} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="edition-save">
          {busy ? "A guardar…" : "Guardar edição"}
        </button>
        {e.status !== "published" ? (
          <button onClick={() => save({ status: "published" })} className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-700 text-emerald-800 text-xs uppercase tracking-[0.2em] hover:bg-emerald-700 hover:text-white transition-colors" data-testid="edition-publish">
            <Send className="h-3.5 w-3.5" /> Publicar
          </button>
        ) : (
          <button onClick={() => save({ status: "archived" })} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="edition-archive">
            Arquivar
          </button>
        )}
        <span className={`ml-auto text-xs px-2 py-1 ${STATUS_LABEL[e.status]?.klass || ""}`}>{STATUS_LABEL[e.status]?.label || e.status}</span>
      </div>
    </div>
  );
}

// ─── Main manager (tabs: modelos / edições) ─────────────────────────────────
export default function ItinerariesManager() {
  const [tab, setTab] = useState("editions");
  const [templates, setTemplates] = useState([]);
  const [editions, setEditions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null); // null | {} | template
  const [openEdition, setOpenEdition] = useState(null);
  const [newEdition, setNewEdition] = useState(null); // {template_id, title, doc_days_before, capacity}

  const load = async () => {
    setLoading(true);
    try {
      const [t, ed, mem] = await Promise.all([
        axios.get(`${M}/templates`, { withCredentials: true }),
        axios.get(`${M}/editions`, { withCredentials: true }),
        axios.get(`${M}/members`, { withCredentials: true }),
      ]);
      setTemplates(t.data.items || []);
      setEditions(ed.data.items || []);
      setMembers(mem.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sem acesso à gestão de percursos.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Apagar o modelo "${t.title}"? As edições já criadas não são afetadas.`)) return;
    try { await axios.delete(`${M}/templates/${t.id}`, { withCredentials: true }); load(); }
    catch { toast.error("Erro a apagar."); }
  };

  const deleteEdition = async (e) => {
    if (!window.confirm(`Apagar a edição "${e.title}"? Os participantes deixam de receber emails.`)) return;
    try { await axios.delete(`${M}/editions/${e.id}`, { withCredentials: true }); setOpenEdition(null); load(); }
    catch { toast.error("Erro a apagar."); }
  };

  const createEdition = async () => {
    if (!newEdition?.template_id || !newEdition?.title?.trim()) { toast.error("Escolha o modelo e dê um nome à edição."); return; }
    try {
      const r = await axios.post(`${M}/editions`, {
        template_id: newEdition.template_id,
        title: newEdition.title,
        doc_days_before: Number(newEdition.doc_days_before) || 7,
        ...(newEdition.capacity ? { capacity: Number(newEdition.capacity) } : {}),
      }, { withCredentials: true });
      setNewEdition(null);
      await load();
      setOpenEdition(r.data);
      toast.success("Edição criada — defina agora as datas das sessões.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a criar edição."); }
  };

  if (loading) return <p className="text-[hsl(var(--muted-foreground))] py-8">A carregar…</p>;

  return (
    <div data-testid="itineraries-manager">
      <div className="flex items-center gap-2 border-b border-[hsl(var(--border))]">
        {[{ id: "editions", label: `Edições (${editions.length})` }, { id: "templates", label: `Modelos (${templates.length})` }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === t.id ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] font-medium" : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
            data-testid={`itineraries-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "templates" && (
        <div className="mt-6 space-y-4">
          {editingTemplate !== null ? (
            <TemplateForm initial={editingTemplate.id ? editingTemplate : null}
              onSaved={() => { setEditingTemplate(null); load(); }}
              onCancel={() => setEditingTemplate(null)} />
          ) : (
            <button onClick={() => setEditingTemplate({})} className="btn-primary" data-testid="new-template-btn">
              <Plus className="h-3.5 w-3.5" /> Novo modelo
            </button>
          )}
          {templates.map((t) => (
            <div key={t.id} className="bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3" data-testid={`template-${t.id}`}>
              <div>
                <div className="font-medium text-sm"><BookOpen className="inline h-3.5 w-3.5 mr-1.5 text-[hsl(var(--accent))]" />{t.title}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{(t.sessions || []).length} sessões{t.description ? ` · ${t.description.slice(0, 100)}` : ""}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditingTemplate(t)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]" title="Editar" data-testid={`template-edit-${t.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => deleteTemplate(t)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" title="Apagar">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {!templates.length && <p className="text-sm text-[hsl(var(--muted-foreground))]">Ainda não há modelos. Crie o primeiro percurso.</p>}
        </div>
      )}

      {tab === "editions" && (
        <div className="mt-6 space-y-4">
          {openEdition ? (
            <EditionEditor edition={openEdition} members={members}
              onChanged={load} onClose={() => { setOpenEdition(null); load(); }} />
          ) : newEdition ? (
            <div className="bg-white border border-[hsl(var(--accent))] p-6 space-y-3" data-testid="new-edition-form">
              <div className="overline">Nova edição</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={newEdition.template_id} onChange={(e) => setNewEdition({ ...newEdition, template_id: e.target.value })}
                  className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="new-edition-template">
                  <option value="">— Modelo de percurso —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title} ({(t.sessions || []).length} sessões)</option>)}
                </select>
                <input value={newEdition.title} onChange={(e) => setNewEdition({ ...newEdition, title: e.target.value })}
                  placeholder='Nome da edição — ex.: "Outono 2026"'
                  className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="new-edition-title" />
                <input type="number" min="0" max="60" value={newEdition.doc_days_before}
                  onChange={(e) => setNewEdition({ ...newEdition, doc_days_before: e.target.value })}
                  placeholder="Dias de antecedência do documento (7)"
                  className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="new-edition-days" />
                <input type="number" min="1" value={newEdition.capacity}
                  onChange={(e) => setNewEdition({ ...newEdition, capacity: e.target.value })}
                  placeholder="Lotação (opcional)"
                  className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="new-edition-capacity" />
              </div>
              <div className="flex items-center gap-3">
                <button onClick={createEdition} className="btn-primary" data-testid="new-edition-create">Criar edição</button>
                <button onClick={() => setNewEdition(null)} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">Cancelar</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setNewEdition({ template_id: "", title: "", doc_days_before: 7, capacity: "" })}
              className="btn-primary" data-testid="new-edition-btn" disabled={!templates.length}
              title={!templates.length ? "Crie primeiro um modelo de percurso" : ""}>
              <Plus className="h-3.5 w-3.5" /> Nova edição
            </button>
          )}
          {!templates.length && !editions.length && (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Comece por criar um <strong>modelo</strong> no separador "Modelos"; depois lance edições com datas reais.</p>
          )}
          {!openEdition && editions.map((e) => (
            <div key={e.id} className="bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3" data-testid={`edition-${e.id}`}>
              <div className="cursor-pointer flex-1" onClick={async () => {
                const r = await axios.get(`${M}/editions/${e.id}`, { withCredentials: true });
                setOpenEdition(r.data);
              }}>
                <div className="font-medium text-sm">{e.itinerary_title} · <span className="text-[hsl(var(--accent))]">{e.title}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {(e.sessions || []).filter((s) => s.date).length}/{(e.sessions || []).length} sessões agendadas · {e.participant_count} participante(s)
                  {e.capacity ? ` / ${e.capacity}` : ""} · doc {e.doc_days_before}d antes
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs px-2 py-1 ${STATUS_LABEL[e.status]?.klass || ""}`}>{STATUS_LABEL[e.status]?.label || e.status}</span>
                <button onClick={() => deleteEdition(e)} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" title="Apagar" data-testid={`edition-delete-${e.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
